import { Attribute, Measure, useModelService } from 'services/model/model.service';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { useLocation, useNavigate } from 'react-router-dom';
import { IntlShape, useIntl } from 'react-intl';
import { useContext } from 'react';
import { TenantConfig, TenantConfigContext } from 'context/TenantConfigContext';
import { useAppStore } from 'hooks/state/useAppStore';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { createArticleImageCell, createMeasureCell, sortComparisonCell } from 'components/table/DataTableUtil';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';

interface TopReturnedProductsRow {
  image: string;
  prdNumber: string;
  prdName: string;
  prdCategory: string;
  retQty: number;
  retQtyCompare: number;
  retRate: number;
  retRateCompare: number;
  retGmv: number;
  retGmvCompare: number;
  slsQty: number;
  slsQtyCompare: number;
  netSlsGmv: number;
  netSlsGmvCompare: number;
}

const columnHelper = createColumnHelper<TopReturnedProductsRow>();
const buildColumns = (
  attributes: Attribute[],
  measures: Measure[],
  intl: IntlShape,
  tenantContext: TenantConfig,
  comparisonActive: boolean
) => [
  columnHelper.accessor('image', {
    enableSorting: false,
    header: () => intl.formatMessage({ id: 'general.label.image' }),
    cell: context => createArticleImageCell(context, tenantContext),
  }),
  columnHelper.accessor('prdNumber', {
    header: () => attributes[1].name,
    cell: context => context.getValue(),
  }),
  columnHelper.accessor('prdName', {
    header: () => attributes[2].name,
    cell: context => context.getValue(),
  }),
  columnHelper.accessor('prdCategory', {
    header: () => attributes[3].name,
    cell: context => context.getValue(),
  }),
  columnHelper.accessor(row => ({ value: row.retQty, compareValue: row.retQtyCompare }), {
    id: measures[0].code,
    header: () => measures[0].metric.name,
    cell: context => createMeasureCell(context, measures[0], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.retRate, compareValue: row.retRateCompare }), {
    id: measures[1].code,
    header: () => measures[1].metric.name,
    cell: context => createMeasureCell(context, measures[1], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.retGmv, compareValue: row.retGmvCompare }), {
    id: measures[2].code,
    header: () => measures[2].metric.name,
    cell: context => createMeasureCell(context, measures[2], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.slsQty, compareValue: row.slsQtyCompare }), {
    id: measures[3].code,
    header: () => measures[3].metric.name,
    cell: context => createMeasureCell(context, measures[3], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.netSlsGmv, compareValue: row.netSlsGmvCompare }), {
    id: measures[4].code,
    header: () => measures[4].metric.name,
    cell: context => createMeasureCell(context, measures[4], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
];

export function TopReturnedProductsTable() {
  const intl = useIntl();
  const tenantContext = useContext(TenantConfigContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { getMeasures, getAttributes } = useModelService();

  const attributes = getAttributes(['artImageUrl', 'prdNumber', 'prdName', 'prdCategory']);

  const [artImageUrl, prdNumber, prdName, prdCategory] = attributes;

  const measures = getMeasures(['retQtyExpOD', 'retQtyRateExpOD', 'retGmvValExpOD', 'slsQtyOD', 'netSlsGmvValExpOD']);

  const [retQty, retQtyRate, retGmvVal, slsQty, netSlsGmvVal] = measures;

  const { fetchData } = useProDataService(Service.Products);
  const requestData = useDataRequest();

  const { products } = useAppStore();
  const productNumbers = products?.map(p => p);

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData(`reports/returns/${retQty.code}`, {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers,
        },
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );
      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-products-returns-report']
  );

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const rows =
    reportState.data?.response.sets.products.map(row => {
      const productRow: TopReturnedProductsRow = {
        image: artImageUrl.getValue(row.attributes),
        prdNumber: prdNumber.getValue(row.attributes),
        prdName: prdName.getValue(row.attributes),
        prdCategory: prdCategory.getValue(row.attributes),
        retQty: retQty.getValue(row.values),
        retQtyCompare: retQty.getValue(row.compareValues),
        retRate: retQtyRate.getValue(row.values),
        retRateCompare: retQtyRate.getValue(row.compareValues),
        retGmv: retGmvVal.getValue(row.values),
        retGmvCompare: retGmvVal.getValue(row.compareValues),
        slsQty: slsQty.getValue(row.values),
        slsQtyCompare: slsQty.getValue(row.compareValues),
        netSlsGmv: netSlsGmvVal.getValue(row.values),
        netSlsGmvCompare: netSlsGmvVal.getValue(row.compareValues),
      };

      return productRow;
    }) ?? [];

  const isComparisonActive = reportState.data?.isComparisonActive ?? false;

  const columns = buildColumns(
    attributes,
    measures,
    intl,
    tenantContext,
    isComparisonActive
  ) as ColumnDef<TopReturnedProductsRow>[];

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Products,
      measuresGroup: 'returns',
      tableName: 'Product Returns',
      isProReport: true,
    },
  };

  const navigateToProductDetails = (productNumber: string) => {
    const url = `/product/${encodeURIComponent(productNumber)}${location.search}`;
    navigate(url);
  };

  const onRowClick = (row: Row<TopReturnedProductsRow>) => {
    const prdNumberValue = row.getValue(prdNumber.code);
    navigateToProductDetails(prdNumberValue as string);
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId="insights.product.reports.returns.description"
        rows={rows}
        columns={columns}
        exportConfigOptions={exportConfigOptions}
        defaultSortingState={[{ id: measures[0].code, desc: true }]}
        onRowClick={onRowClick}
        isError={reportState.isError}
        errorMessage={reportState.error}
      />
    </DataTableContextProvider>
  );
}
